import { MetricBand } from "~/components/visibilityPerformance/bands";

import styles from "./GainSparkline.module.scss";
import ScoreDonut, { COMPACT_DONUT_PROPS } from "./ScoreDonut";

interface Props {
  /** The measured "before" value, 0-100. The "after" is always 100. */
  before: number;
  band: MetricBand;
  /** Metric name used in the tooltip, e.g. "Visibility Score". */
  label: string;
}

// Band classes set both `color` (for the number) and `fill` (for the SVG dot)
// via theme-aware CSS vars — see the .module.scss.
const BAND_CLASS: Record<MetricBand, string> = {
  good: styles.good,
  warn: styles.warn,
  bad: styles.bad,
};

// Stroke variant of the band colour, for the "before" portion of the line.
const LINE_BAND_CLASS: Record<MetricBand, string> = {
  good: styles.lineGood,
  warn: styles.lineWarn,
  bad: styles.lineBad,
};

const W = 60;
const H = 26;
const PAD = 4;
const ARROW_LEN = 8;
const ARROW_HALF = 4;

const GainSparkline = ({ before, band, label }: Props) => {
  // Gentle concave "lift": flat start that rises into the gain. Control point
  // sits at the start height, partway across.
  const x1 = PAD;
  const y1 = PAD + (1 - before / 100) * (H - PAD * 2);
  const p1x = W;
  const p1y = PAD;
  const cx = x1 + (p1x - x1) * 0.55;
  const cy = y1;

  // Split the curve at the before/gain ratio (de Casteljau) so the first
  // `before`% is the band colour and the gain is green. Clamp so the split
  // never runs into the arrowhead.
  const t = Math.min(before / 100, 0.9);
  const lerp = (a: number, b: number) => a + (b - a) * t;
  const ax = lerp(x1, cx);
  const ay = lerp(y1, cy);
  const bx = lerp(cx, p1x);
  const by = lerp(cy, p1y);
  const sx = lerp(ax, bx);
  const sy = lerp(ay, by);

  // Arrowhead follows the curve's end tangent (direction P1 - C).
  const dxe = p1x - cx;
  const dye = p1y - cy;
  const le = Math.hypot(dxe, dye) || 1;
  const ux = dxe / le;
  const uy = dye / le;
  const px = -uy;
  const py = ux;
  const baseX = p1x - ux * ARROW_LEN;
  const baseY = p1y - uy * ARROW_LEN;

  return (
    <span
      className={styles.wrap}
      data-tooltip-id="vm-header-tooltip"
      data-tooltip-content={`Your ${label} was ${before}% and is now 100%, a ${100 - before}% improvement.`}
    >
      <ScoreDonut
        value={before}
        band={band}
        suffix=""
        {...COMPACT_DONUT_PROPS}
      />
      <svg width={W} height={H} className={styles.spark}>
        <path
          className={`${LINE_BAND_CLASS[band]} ${styles.curvePath}`}
          d={`M ${x1},${y1} Q ${ax},${ay} ${sx},${sy}`}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          className={`${styles.line} ${styles.curvePath} ${styles.curvePathGreen}`}
          d={`M ${sx},${sy} Q ${bx},${by} ${baseX},${baseY}`}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle className={BAND_CLASS[band]} cx={x1} cy={y1} r="2.5" />
        <polygon
          className={styles.arrowFill}
          points={`${p1x},${p1y} ${baseX + px * ARROW_HALF},${baseY + py * ARROW_HALF} ${baseX - px * ARROW_HALF},${baseY - py * ARROW_HALF}`}
        />
      </svg>
      <span className={styles.after}>+{100 - before}%</span>
    </span>
  );
};

export default GainSparkline;
